/**
 * TransactionItem Component
 *
 * Displays a single transaction matching the design mockup:
 * - Description as main text
 * - "Category · Account" as subtitle
 * - Colored category icon
 * - Amount with type-based coloring
 */

import { Transaction, Category, Account } from '@services/db';
import { usePrivacyMode } from '@hooks/usePrivacyMode';
import { formatCurrency as formatCurrencyIntl } from '@/utils/format';
import { splitHighlight } from '@services/searchQuery';
import { getCategoryIcon as getCategoryIconShared } from '@/utils/categoryIcons';
import { categoryDisplayName } from '@/i18n';
import './TransactionItem.css';

interface TransactionItemProps {
  transaction: Transaction;
  category?: Category;
  account?: Account;
  toAccount?: Account;
  onClick?: () => void;
  /** Raw search terms to highlight inside the description (active search). */
  highlightTerms?: string[];
}

/**
 * Format currency for display.
 */
function formatCurrency(amount: number, currency: string): string {
  // Central locale-aware formatter (utils/format.ts) - never inline Intl here
  return formatCurrencyIntl(amount, currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Rich category color palette for dark theme.
 * Each category gets a distinct, vibrant background color.
 */
const CATEGORY_COLORS: Record<string, string> = {
  // Expenses
  'Food': '#c75050',           // Deep coral
  'Food & Dining': '#c75050',
  'Groceries': '#c75050',
  'Housing': '#5b6b9a',        // Steel blue
  'Rent': '#5b6b9a',
  'Transportation': '#b8941f', // Deep gold
  'Transport': '#b8941f',
  'Entertainment': '#9a5070',  // Deep rose
  'Shopping': '#2a8a84',       // Deep teal
  'Utilities': '#6b7a3d',      // Olive
  'Bills': '#5b6b9a',          // Steel blue
  'Healthcare': '#7a5b9a',     // Purple
  'Health': '#7a5b9a',
  'Insurance': '#5b6b9a',      // Steel blue
  'Subscriptions': '#9a5070',  // Deep rose
  'Education': '#2a6a8a',      // Ocean blue
  'Other': '#4a5568',          // Slate
  // Income
  'Salary': '#d97706',         // Amber/orange
  'Income': '#d97706',
  'Freelance': '#d97706',
  'Investment': '#059669',     // Emerald
  'Gift': '#9a5070',           // Deep rose
  'Refund': '#2a8a84',         // Deep teal
};

/**
 * Get icon for a category - shared resolver (exact + keyword matching).
 */
function getCategoryIcon(category?: Category): string {
  return getCategoryIconShared(category?.name, category?.icon);
}

/**
 * Get transfer icon.
 */
function getTransferIcon(): string {
  return '↔️';
}

/**
 * Render a label with <mark> around search-term matches.
 * Falls back to plain text when there are no terms or no hits.
 */
function renderHighlighted(label: string, terms?: string[]) {
  if (!terms || terms.length === 0) return label;
  const segments = splitHighlight(label, terms);
  if (segments.length === 1 && !segments[0].match) return label;
  return segments.map((seg, i) =>
    seg.match ? (
      <mark key={i} className="txn-highlight">{seg.text}</mark>
    ) : (
      <span key={i}>{seg.text}</span>
    )
  );
}

export function TransactionItem({
  transaction,
  category,
  account,
  toAccount,
  onClick,
  highlightTerms,
}: TransactionItemProps) {
  const { isPrivacyMode } = usePrivacyMode();
  const isIncome = transaction.type === 'income';
  const isExpense = transaction.type === 'expense';
  const isTransfer = transaction.type === 'transfer';
  const isSkipped = transaction.wasSkipped === true;

  const amountClass = isIncome
    ? 'text-income'
    : isExpense
    ? 'text-expense'
    : 'text-transfer';

  const amountPrefix = isIncome ? '+' : isExpense ? '-' : '';

  // Main label: description or transfer destination
  let mainLabel = transaction.description || 'No description';
  if (isTransfer && toAccount) {
    mainLabel = `Transfer to ${toAccount.name}`;
  }

  // Subtitle: "Category · Account" or "From -> To" for transfers
  let subtitle = '';
  if (isTransfer) {
    subtitle = `${account?.name || 'Unknown'} → ${toAccount?.name || 'Unknown'}`;
  } else {
    const parts = [];
    if (category?.name) parts.push(categoryDisplayName(category.name));
    if (account?.name) parts.push(account.name);
    subtitle = parts.join(' · ');
  }

  // Icon and background color
  const icon = isTransfer ? getTransferIcon() : getCategoryIcon(category);

  // Check if recurring/scheduled - either has scheduledTransactionId or tagged as scheduled/recurring
  const isRecurring =
    !!transaction.scheduledTransactionId ||
    transaction.tags?.some(t => t.startsWith('scheduled') || t.startsWith('recurring'));

  // Get rich category color from palette, or fall back to type-based colors
  const getCategoryBgColor = (): string => {
    // For transfers, use transfer color
    if (isTransfer) return 'rgba(78, 205, 196, 0.2)'; // Teal with opacity

    // Try category name in our color palette
    if (category?.name && CATEGORY_COLORS[category.name]) {
      return `${CATEGORY_COLORS[category.name]}30`; // 30 = ~19% opacity
    }

    // Fall back to category's stored color if available
    if (category?.color) {
      return `${category.color}30`;
    }

    // Final fallback based on transaction type
    return isIncome ? 'rgba(217, 119, 6, 0.2)' : 'rgba(199, 80, 80, 0.2)';
  };

  const iconBgColor = getCategoryBgColor();

  return (
    <div
      className={`transaction-item ${isSkipped ? 'skipped' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="transaction-left">
        <div
          className="transaction-icon"
          style={{ background: iconBgColor }}
        >
          {icon}
        </div>
        <div className="transaction-info">
          <span className="transaction-label">
            {renderHighlighted(mainLabel, highlightTerms)}
            {isSkipped && <span className="skipped-indicator">Skipped</span>}
          </span>
          <span className="transaction-meta">{subtitle}</span>
        </div>
      </div>
      <div className="transaction-right">
        {isRecurring && <span className="recurring-badge" title="Recurring">↻</span>}
        <span className={`transaction-amount ${amountClass}`}>
          {isPrivacyMode && isIncome ? (
            <span className="private-amount masked">+••••</span>
          ) : (
            <>
              {amountPrefix}
              {formatCurrency(transaction.amount, transaction.currency)}
            </>
          )}
        </span>
      </div>
    </div>
  );
}

/**
 * TransactionItemSkeleton - Loading placeholder
 */
export function TransactionItemSkeleton() {
  return (
    <div className="transaction-item transaction-item-skeleton">
      <div className="transaction-left">
        <div className="skeleton skeleton-icon" />
        <div className="transaction-info">
          <div className="skeleton skeleton-label" />
          <div className="skeleton skeleton-meta" />
        </div>
      </div>
      <div className="transaction-right">
        <div className="skeleton skeleton-amount" />
      </div>
    </div>
  );
}
